import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Sparkline from '../viz/Sparkline';
import RiskTrendPill from './RiskTrendPill';
import { useThemeColors } from '../../hooks/useThemeColors';
import './AssetsWidget.css';

/**
 * AssetsWidget (PRD-68 revised) — promoted Assets table that replaces both
 * the old Top Movers widget and the bottom-of-page Assets table.
 *
 * Columns: Asset | Trajectory | Score | Change | Risk | Findings | Ports | Last scan
 *
 * Polarity (PRD-51): the server-provided `trend` field is authoritative.
 * - worsening → red ↑ Increasing
 * - improving → green ↓ Decreasing
 * - stable    → muted = Stable
 *
 * The Change column arrow (▲/▼/=) is sign-only arithmetic and intentionally
 * has NO health color — that's what the Risk pill is for. This is the
 * polarity collision fix from PRD-68.
 */

const DEFAULT_VISIBLE = 10;

function getAssetTypeLabel(type) {
    switch (type) {
        case 'domain': return 'Domain';
        case 'ip': return 'IP';
        case 'cidr': return 'CIDR';
        default: return type || 'Asset';
    }
}

function getRelativeScanTime(dateStr) {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (Number.isNaN(diff)) return '—';
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
}

function ChangeCell({ delta }) {
    // Sign-only, NO health color. Purely arithmetic per PRD §3.1.
    const d = Number.isFinite(delta) ? delta : 0;
    let glyph;
    let text;
    if (d > 0) {
        glyph = '▲';        // ▲
        text = `+${d}`;
    } else if (d < 0) {
        glyph = '▼';        // ▼
        text = `−${Math.abs(d)}`; // proper minus sign
    } else {
        glyph = '=';
        text = '0';
    }
    return (
        <span className="aw-change">
            <span className="aw-change__glyph" aria-hidden="true">{glyph}</span>
            <span className="aw-change__value">{text}</span>
        </span>
    );
}

function FindingsBadges({ asset }) {
    const c = asset.criticalCount || 0;
    const h = asset.highCount || 0;
    const m = asset.mediumCount || 0;
    if (c === 0 && h === 0 && m === 0) {
        return <span className="aw-no-findings">—</span>;
    }
    return (
        <div className="aw-findings-badges">
            {c > 0 && <span className="cm-severity-badge critical">{c}C</span>}
            {h > 0 && <span className="cm-severity-badge high">{h}H</span>}
            {m > 0 && <span className="cm-severity-badge medium">{m}M</span>}
        </div>
    );
}

function EmptyState({ meta, windowDays }) {
    // PRD §3.6 / PRD-67 §4.3 three-class taxonomy keyed off meta.assetsTrajectory.
    if (!meta) {
        return (
            <div className="aw-empty">
                <p>No assets yet.</p>
                <Link to="/assets" className="aw-empty__link">Add your first asset</Link>
            </div>
        );
    }
    const historyEntriesFound = meta.historyEntriesFound ?? 0;
    const eligibleForDelta = meta.eligibleForDelta ?? 0;
    const wd = meta.windowDays ?? windowDays ?? 7;

    if (historyEntriesFound === 0) {
        return (
            <div className="aw-empty">
                <p>Run your first scan to see trajectory data.</p>
                <Link to="/assets" className="aw-empty__link">Go to Asset Management</Link>
            </div>
        );
    }
    if (eligibleForDelta === 0) {
        return (
            <div className="aw-empty">
                <p>
                    Trajectory compares scans ≥{wd}d apart — not enough history yet across your assets.
                </p>
            </div>
        );
    }
    // Shouldn't reach here for "no rows" case — when data exists, the parent
    // still renders the (stable-only) rows. This is just a final fallback.
    return (
        <div className="aw-empty">
            <p>No assets to display.</p>
        </div>
    );
}

function WindowSelector({ windowDays, onWindowChange }) {
    const opts = [7, 30, 90];
    return (
        <div className="aw-window-selector" role="tablist" aria-label="Trajectory window">
            {opts.map((d) => (
                <button
                    key={d}
                    role="tab"
                    aria-selected={d === windowDays}
                    className={`aw-window-pill ${d === windowDays ? 'is-active' : ''}`}
                    onClick={() => onWindowChange && onWindowChange(d)}
                    type="button"
                >
                    {d}d
                </button>
            ))}
        </div>
    );
}

function AssetsWidget({
    assets = [],
    total,
    windowDays = 7,
    onWindowChange,
    onAssetClick,
    onQuickScan,
    scanningAssets,
    meta,
}) {
    // Theme colors only used for ariaLabel construction below (no SVG inline styles here)
    useThemeColors();

    const [expanded, setExpanded] = useState(false);

    const totalCount = Number.isFinite(total) ? total : assets.length;
    const visibleAssets = useMemo(() => {
        if (expanded) return assets;
        return assets.slice(0, DEFAULT_VISIBLE);
    }, [assets, expanded]);

    const visibleCount = visibleAssets.length;

    // Empty case: no rows at all.
    if (assets.length === 0) {
        return (
            <div className="assets-widget">
                <div className="aw-header">
                    <div className="aw-header__left">
                        <p className="aw-subtitle">
                            All assets sorted by current risk score · 0 of 0 shown · {windowDays}-day trend
                        </p>
                    </div>
                    <div className="aw-header__right">
                        <WindowSelector windowDays={windowDays} onWindowChange={onWindowChange} />
                    </div>
                </div>
                <EmptyState meta={meta} windowDays={windowDays} />
            </div>
        );
    }

    return (
        <div className="assets-widget">
            <div className="aw-header">
                <div className="aw-header__left">
                    <p className="aw-subtitle">
                        All assets sorted by current risk score · {visibleCount} of {totalCount} shown · {windowDays}-day trend
                    </p>
                </div>
                <div className="aw-header__right">
                    <WindowSelector windowDays={windowDays} onWindowChange={onWindowChange} />
                </div>
            </div>

            <div className="aw-table-wrapper">
                <table className="aw-table">
                    <thead>
                        <tr>
                            <th className="aw-col-asset">Asset</th>
                            <th className="aw-col-trajectory">Trajectory</th>
                            <th className="aw-col-score">Score</th>
                            <th className="aw-col-change">Change</th>
                            <th className="aw-col-risk">Risk</th>
                            <th className="aw-col-findings">Findings</th>
                            <th className="aw-col-ports">Ports</th>
                            <th className="aw-col-scan">Last scan</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleAssets.map((asset) => {
                            const assetId = asset.assetId;
                            // Server contract (PRD-68 §3.2): currentScore, previousScore,
                            // delta, trend, trajectory, driver, windowDays.
                            const currentScore = asset.currentScore != null
                                ? asset.currentScore
                                : (asset.riskScore != null ? asset.riskScore : null);
                            const previousScore = asset.previousScore != null
                                ? asset.previousScore
                                : null;
                            const delta = Number.isFinite(asset.delta) ? asset.delta : 0;
                            const trend = asset.trend || 'stable';
                            const trajectory = Array.isArray(asset.trajectory) ? asset.trajectory : [];
                            const driver = asset.driver || null;
                            const lastScan = asset.lastVulnScanAt
                                || asset.lastDiscoveryAt
                                || asset.lastBaselineAt
                                || null;
                            const isScanning = scanningAssets && scanningAssets.has(assetId);

                            // Truncate driver to ~50 chars.
                            const driverDisplay = driver && driver.length > 50
                                ? `${driver.slice(0, 49)}…`
                                : driver;

                            const scoreTooltip = previousScore != null
                                ? `${previousScore} → ${currentScore}`
                                : `Current score: ${currentScore ?? '—'}`;

                            return (
                                <tr
                                    key={assetId}
                                    className="aw-row"
                                    tabIndex={0}
                                    onClick={() => onAssetClick && onAssetClick(assetId)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onAssetClick && onAssetClick(assetId);
                                        }
                                    }}
                                >
                                    <td className="aw-col-asset">
                                        <div className="aw-asset-cell">
                                            <div className="aw-asset-line1">
                                                <span className="aw-asset-name">{asset.value}</span>
                                                <span className="aw-asset-type-chip">
                                                    {getAssetTypeLabel(asset.assetType)}
                                                </span>
                                            </div>
                                            {driverDisplay && (
                                                <div
                                                    className="aw-asset-driver"
                                                    title={driver}
                                                >
                                                    {driverDisplay}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="aw-col-trajectory">
                                        <Sparkline
                                            data={trajectory}
                                            width={120}
                                            height={28}
                                            trend={trend}
                                            title={`Risk trajectory for ${asset.value}`}
                                            desc={
                                                trajectory.length >= 2
                                                    ? `Risk score trajectory over ${windowDays} days, ${trend}.`
                                                    : 'Not enough data to render a trajectory.'
                                            }
                                        />
                                    </td>
                                    <td className="aw-col-score">
                                        <span className="aw-score-value" title={scoreTooltip}>
                                            {currentScore != null ? currentScore : '—'}
                                        </span>
                                    </td>
                                    <td className="aw-col-change">
                                        <ChangeCell delta={delta} />
                                    </td>
                                    <td className="aw-col-risk">
                                        <RiskTrendPill trend={trend} />
                                    </td>
                                    <td className="aw-col-findings">
                                        <FindingsBadges asset={asset} />
                                    </td>
                                    <td className="aw-col-ports">{asset.openPortCount || 0}</td>
                                    <td className="aw-col-scan">
                                        <div className="aw-scan-cell">
                                            <span className="aw-scan-time">{getRelativeScanTime(lastScan)}</span>
                                            {onQuickScan && (
                                                <button
                                                    type="button"
                                                    className="aw-quick-scan-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onQuickScan(assetId, asset.value);
                                                    }}
                                                    disabled={isScanning}
                                                    title={`Trigger vulnerability scan for ${asset.value}`}
                                                    aria-label={`Quick scan ${asset.value}`}
                                                >
                                                    {isScanning ? '…' : 'Scan'}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {totalCount > DEFAULT_VISIBLE && (
                <div className="aw-expand-row">
                    <button
                        type="button"
                        className="aw-expand-btn"
                        onClick={() => setExpanded((prev) => !prev)}
                        aria-expanded={expanded}
                    >
                        {expanded ? `Show top ${DEFAULT_VISIBLE}` : `Show all ${totalCount}`}
                    </button>
                </div>
            )}
        </div>
    );
}

export default AssetsWidget;
